import {
  init,
  miniApp,
  postEvent,
  retrieveLaunchParams,
  supports,
  swipeBehavior,
  viewport,
} from '@telegram-apps/sdk';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app/App.js';
import './shared/ui/theme.css';
import './shared/ui/effects.css';

/**
 * Точка входа Mini App.
 *
 * SDK инициализируется до монтирования: тема и размеры должны быть
 * известны к первому кадру, иначе экран моргнёт чужими цветами.
 */
function setupTelegram(): void {
  try {
    init();
    if (miniApp.mountSync.isAvailable()) {
      miniApp.mountSync();
      miniApp.bindCssVars();
      const canvas = getComputedStyle(document.documentElement).getPropertyValue('--canvas').trim();
      if (miniApp.setBackgroundColor.isAvailable()) miniApp.setBackgroundColor(canvas);
      if (miniApp.setBottomBarColor.isAvailable()) miniApp.setBottomBarColor(canvas);
      if (miniApp.setHeaderColor.isAvailable() && miniApp.setHeaderColor.supports.rgb()) {
        miniApp.setHeaderColor(canvas);
      }
    }
    // Раскрытие по высоте остаётся запасным режимом на старых клиентах.
    if (viewport.expand.isAvailable()) viewport.expand();
    if (viewport.mount.isAvailable()) {
      void viewport
        .mount({ timeout: 2000 })
        .then(async () => {
          if (viewport.bindCssVars.isAvailable()) viewport.bindCssVars();
          if (viewport.requestFullscreen.isAvailable() && !viewport.isFullscreen()) {
            await viewport.requestFullscreen({ timeout: 2000 });
          }
        })
        .catch(() => {
          // Отказ Telegram или отсутствие ответа не блокируют запуск игры.
          if (!viewport.isMounted()) {
            const launch = retrieveLaunchParams();
            if (
              launch.tgWebAppFullscreen &&
              supports('web_app_exit_fullscreen', launch.tgWebAppVersion)
            ) {
              // Без размеров безопасных зон нельзя оставлять кнопки под шапкой Telegram.
              postEvent('web_app_exit_fullscreen');
            }
          }
        });
    }
    // Свайп вниз по экрану не стягивает окно игры: на телефоне прокрутка
    // списка закрывала Mini App и экран дёргался вслед за пальцем.
    if (swipeBehavior.mount.isAvailable()) {
      swipeBehavior.mount();
      if (swipeBehavior.disableVertical.isAvailable()) swipeBehavior.disableVertical();
    }
  } catch {
    // Открыто вне Telegram: работаем на запасных цветах, экран сам объяснит ситуацию.
  }
}

setupTelegram();

/*
 * Первый кадр рисуется уже шрифтом Inter. Запасной шрифт шире: строки
 * переносились иначе, и когда Inter догружался, текст и список шагов
 * прыгали на 20 px. Ждём не дольше 600 мс — на медленной сети лучше
 * показать игру запасным шрифтом, чем пустой экран.
 */
async function fontsReady(): Promise<void> {
  if (!('fonts' in document)) return;
  const load = Promise.all([
    document.fonts.load('600 16px Inter', 'Аа'),
    document.fonts.load('600 16px Inter', 'Aa'),
  ]);
  await Promise.race([load, new Promise((resolve) => setTimeout(resolve, 600))]).catch(() => {});
}

const container = document.getElementById('root');
if (container === null) throw new Error('Контейнер #root не найден');
const root = container;

void fontsReady().then(() => {
  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
});
